import { useEffect, useState } from "react";
import { Camera, Loader2, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import api, { formatApiError } from "../api";

const fmtWaktu = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" });
  } catch { return iso; }
};

export const FotoImg = ({ fotoId, className, testId }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/foto/${fotoId}/file`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fotoId]);
  if (failed) return <div className={`${className} bg-zinc-100 flex items-center justify-center text-xs text-zinc-400`}>Gagal dimuat</div>;
  if (!src) return <div className={`${className} bg-zinc-100 flex items-center justify-center`}><Loader2 className="w-5 h-5 animate-spin text-zinc-300" /></div>;
  return <img data-testid={testId} src={src} alt="Bukti timbang" className={className} />;
};

export const FotoBuktiDialog = ({ trip, onClose }) => {
  const [fotos, setFotos] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    api.get(`/trips/${trip.id}/foto`).then(({ data }) => setFotos(data)).catch(() => setFotos([]));
    api.get(`/trips/${trip.id}/foto?deleted=1`).then(({ data }) => setHidden(data)).catch(() => setHidden([]));
  };

  useEffect(() => {
    if (!trip) return;
    setFotos(null);
    setHidden([]);
    api.get(`/trips/${trip.id}/foto`).then(({ data }) => setFotos(data)).catch(() => setFotos([]));
    api.get(`/trips/${trip.id}/foto?deleted=1`).then(({ data }) => setHidden(data)).catch(() => setHidden([]));
  }, [trip]);

  const handleDelete = async () => {
    try {
      await api.delete(`/foto/${deleteTarget.id}`);
      toast.success("Foto disembunyikan dari daftar bukti");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleRestore = async (f) => {
    try {
      await api.put(`/foto/${f.id}/pulihkan`);
      toast.success("Foto dipulihkan ke daftar bukti");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!trip} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="foto-bukti-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-emerald-700" /> Foto Bukti Timbang — {trip?.ID_Trip}
          </DialogTitle>
          <DialogDescription>Foto struk timbangan yang diunggah supir untuk trip ini.</DialogDescription>
        </DialogHeader>
        {fotos === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : fotos.length === 0 ? (
          <p data-testid="foto-empty-state" className="text-sm text-zinc-500 py-6 text-center">
            Belum ada foto bukti timbang untuk trip ini.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {fotos.map((f) => (
              <div key={f.id} className="relative group">
                <button onClick={() => setZoom(f)} className="text-left w-full">
                  <FotoImg fotoId={f.id} testId={`foto-thumb-${f.id}`}
                    className="w-full h-36 object-cover rounded-md border border-zinc-200 group-hover:opacity-90 transition-opacity duration-200" />
                  <p className="text-[11px] text-zinc-500 mt-1 truncate">{f.uploaded_name} &bull; {fmtWaktu(f.waktu)}</p>
                </button>
                <button data-testid={`delete-foto-${f.id}`} onClick={() => setDeleteTarget(f)}
                  title="Sembunyikan foto"
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-white/90 text-zinc-500 hover:text-red-600 hover:bg-red-50 border border-zinc-200 transition-colors duration-200 icon-touch">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {hidden.length > 0 && (
          <div data-testid="hidden-foto-section" className="border-t border-zinc-200 pt-3 mt-2 space-y-2">
            <p className="text-xs font-medium text-zinc-500">Foto Tersembunyi ({hidden.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {hidden.map((f) => (
                <div key={f.id} className="relative">
                  <FotoImg fotoId={f.id} testId={`hidden-foto-thumb-${f.id}`}
                    className="w-full h-28 object-cover rounded-md border border-zinc-200 opacity-50 grayscale" />
                  <p className="text-[11px] text-zinc-400 mt-1 truncate">{f.uploaded_name} &bull; {fmtWaktu(f.waktu)}</p>
                  <button data-testid={`restore-foto-${f.id}`} onClick={() => handleRestore(f)}
                    className="absolute top-1.5 right-1.5 flex items-center gap-1 px-2 py-1.5 rounded-md bg-white/95 text-xs font-medium text-emerald-700 hover:bg-emerald-50 border border-emerald-200 transition-colors duration-200">
                    <RotateCcw className="w-3.5 h-3.5" /> Pulihkan
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {zoom && (
          <Dialog open onOpenChange={(v) => !v && setZoom(null)}>
            <DialogContent className="sm:max-w-4xl bg-white">
              <DialogHeader>
                <DialogTitle className="text-sm">{trip?.ID_Trip} &bull; {zoom.uploaded_name} &bull; {fmtWaktu(zoom.waktu)}</DialogTitle>
                <DialogDescription className="sr-only">Tampilan penuh foto bukti timbang</DialogDescription>
              </DialogHeader>
              <FotoImg fotoId={zoom.id} testId="foto-zoom" className="w-full max-h-[70vh] object-contain rounded-md" />
            </DialogContent>
          </Dialog>
        )}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Sembunyikan Foto?</AlertDialogTitle>
              <AlertDialogDescription>
                Foto dari {deleteTarget?.uploaded_name} akan disembunyikan dari daftar bukti dan tidak lagi dilampirkan di PDF surat jalan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-foto">Batal</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-foto" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Sembunyikan
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
